import { useEffect, useState } from 'react';
import usePlacesAutocomplete, { type HookArgs } from 'use-places-autocomplete';
import { SUPPORTED_COUNTRIES } from '@/constants/locations';
import { type GoogleAddressComponentFields } from '@/types/fulfillment';
import * as ErrorReporter from '@/utils/errorReporter';
import { useGoogleMapsLoader } from '@/utils/googleMapsLoader';
import { isValidZip } from '@/utils/locations';

/**
 * takes an array of address components and returns an address object
 */
export const mapAutocompleteAddress = (
  components: google.maps.GeocoderAddressComponent[],
): GoogleAddressComponentFields => {
  const newFormattedAddress: GoogleAddressComponentFields = {};

  components.forEach((component) => {
    // Process each type for this component
    component.types.forEach((type) => {
      // @ts-expect-error: type is cast as string, but since we're working within a returned object, we can ignore this
      newFormattedAddress[type] = component.short_name;
    });
  });

  return newFormattedAddress;
};

/**
 * True when Google returned a `street_number` component (an actual house number), independent of
 * the route name. Checking the component — not a leading digit on the joined street_address —
 * avoids treating a numbered street like "5th Avenue" as if it already had a house number
 * (TEAM-15047). Google can resolve a picked prediction to a route-level match with no
 * `street_number` even when the dropdown text showed one; committing that address drops the house
 * number silently and the diner is later blocked at the delivery-quote step with a generic error.
 */
export const hasStreetNumberComponent = (components: GoogleAddressComponentFields): boolean =>
  Boolean(components.street_number);

/**
 * Ordered fallback component types for resolving a city-like value when the
 * standard locality / sublocality_level_1 components are absent. Mirrors
 * chowly-api's Addresses::Google::CITY_FALLBACK_TYPES so the frontend and
 * backend agree on the resolved city.
 *   postal_town                 - mailing grouping used in some countries (e.g. UK)
 *   sublocality                 - first-order civil entity below a locality
 *   neighborhood                - how Google returns census-designated places (e.g. Saint Johns, FL)
 *   administrative_area_level_3 - minor civil division (e.g. townships)
 *   colloquial_area             - commonly-used alternative name for the entity
 *   administrative_area_level_2 - county; last resort so the city is never blank
 */
const CITY_FALLBACK_TYPES = [
  'postal_town',
  'sublocality',
  'neighborhood',
  'administrative_area_level_3',
  'colloquial_area',
  'administrative_area_level_2',
] as const;

/**
 * Resolve a display city from mapped Google address components, mirroring
 * chowly-api's Addresses::Google.resolve_city. Joins sublocality_level_1 and
 * locality when both are present (e.g. NYC boroughs -> "Brooklyn, New York"),
 * otherwise walks CITY_FALLBACK_TYPES. Returns '' when nothing matches so the
 * city is never the literal string "undefined".
 */
export const resolveCity = (components: GoogleAddressComponentFields): string => {
  const joined = [components.sublocality_level_1, components.locality]
    .filter((part): part is string => Boolean(part))
    .join(', ');

  if (joined) {
    return joined;
  }

  const fallback = CITY_FALLBACK_TYPES.map((type) => {
    const value = components[type];
    // Counties read better without their civil-division suffix
    // (e.g. "St. Johns County" -> "St. Johns").
    return type === 'administrative_area_level_2'
      ? value?.replace(/\s+(County|Parish|Borough|Census Area)$/i, '')
      : value;
  }).find((value): value is string => Boolean(value));

  return fallback ?? '';
};

// "103121736" — how Google returns some US ZIP+4s (see resolvePostalCode).
const UNHYPHENATED_ZIP_PLUS_4 = /^\d{9}$/;

/**
 * Resolve a delivery ZIP/postal code from mapped Google address components.
 * Google omits `postal_code` for some selections (vague or rural places), so
 * the previous `String(postal_code)` coercion emitted the literal string
 * "undefined". That malformed value was carried into the basket and rejected
 * downstream by Koala basket-validate as MalformedPayload (code 9) on
 * #/customer/zip, blocking checkout with a generic invalid-basket error
 * (TEAM-14096). Returns the postal code only when it is a valid US ZIP or
 * Canadian postal code, otherwise '' — never the literal string "undefined".
 *
 * Google also returns some US ZIPs as an unhyphenated ZIP+4 ("103121736" for 200 Nedra Ln, Staten
 * Island). That fails the ZIP check, so the pick lost a ZIP it actually had and the diner was blocked
 * (TEAM-15164); keep its 5-digit ZIP instead.
 */
export const resolvePostalCode = (components: GoogleAddressComponentFields): string => {
  const trimmed = components.postal_code?.trim() ?? '';
  const postalCode = UNHYPHENATED_ZIP_PLUS_4.test(trimmed) ? trimmed.slice(0, 5) : trimmed;
  return isValidZip(postalCode) ? postalCode : '';
};

export interface AddressAutocompleteOptions {
  /**
   * Restrict Google predictions to precise street addresses (Places `types: ['address']`) so a
   * selection resolves to a numbered address rather than a route-level match. Used by the delivery
   * checkout (TEAM-15047); other consumers keep the broader `['geocode']`.
   */
  restrictToStreetAddress?: boolean;
}

/**
 * A wrapper hook to handle loading google maps and returning autocomplete values
 */
export function useAddressAutocomplete({
  restrictToStreetAddress = false,
}: AddressAutocompleteOptions = {}) {
  const [options, setOptions] = useState<HookArgs>({
    initOnMount: false,
  });
  const [ready, setReady] = useState(false);
  const {
    init,
    value,
    suggestions: { loading, data, status },
    setValue: updateValue,
    clearSuggestions,
  } = usePlacesAutocomplete(options);

  const { isLoaded, loadError } = useGoogleMapsLoader();

  useEffect(() => {
    if (isLoaded && !ready) {
      try {
        setOptions({
          requestOptions: {
            sessionToken: new google.maps.places.AutocompleteSessionToken(),
            types: restrictToStreetAddress ? ['address'] : ['geocode'],
            componentRestrictions: {
              country: Object.values(SUPPORTED_COUNTRIES),
            },
          },
          googleMaps: google.maps,
          debounce: 500,
        });
        init();
        setReady(true);
      } catch (e) {
        ErrorReporter.captureException(e);
        console.error(e);
      }
    }
  }, [isLoaded, ready, init, restrictToStreetAddress]);

  return {
    clearSuggestions,
    data,
    loading,
    ready,
    status,
    updateValue,
    value,
    googleMapFailed: Boolean(loadError),
  };
}
